import {
    isCalVerFloorDisabled,
    isReleaseBelow,
    isValidCalVerRelease,
} from '~~/layers/base/utils/compareRelease'
import type { NativeStorePlatform } from '~~/layers/base/stores/forceUpdate.store'
import { useForceUpdateStore } from '~~/layers/base/stores/forceUpdate.store'

const HDR_MIN_IOS = 'x-api-min-app-version-ios'
const HDR_MIN_ANDROID = 'x-api-min-app-version-android'

const DEFAULT_BLOCK_MESSAGE =
    'This version of the app is no longer supported. Please update from your app store.'

type PublicCompat = {
    capacitorApp: boolean
    appVersion: string
}

function getRuntimePublicCompat(): PublicCompat {
    const config = useRuntimeConfig()
    const p = config.public as { NUXT_CAPACITOR_APP?: boolean; appVersion?: string }
    return {
        capacitorApp: Boolean(p.NUXT_CAPACITOR_APP),
        appVersion: (p.appVersion || '').trim(),
    }
}

async function getNativePlatformForPolicy(): Promise<NativeStorePlatform | null> {
    const { Capacitor } = await import('@capacitor/core')
    const platform = Capacitor.getPlatform()
    if (platform === 'ios' || platform === 'android') {
        return platform
    }
    return null
}

/**
 * Attaches SPEC request headers for cross-origin API calls from the Capacitor shell.
 */
export async function appendDesignerCompatibilityRequestHeaders(
    headers: Headers,
    baseURL: string | undefined
): Promise<void> {
    if (!import.meta.client) {
        return
    }
    const { capacitorApp, appVersion } = getRuntimePublicCompat()
    if (!capacitorApp) {
        return
    }
    if (!baseURL) {
        return
    }

    const platform = await getNativePlatformForPolicy()
    if (!platform) {
        return
    }

    headers.set('X-App-Version', appVersion)
    headers.set('X-Client-Platform', platform)
}

function parseMinReleaseFromHeaders(response: Response): {
    ios: string | null
    android: string | null
} {
    return {
        ios: response.headers.get(HDR_MIN_IOS)?.trim() || null,
        android: response.headers.get(HDR_MIN_ANDROID)?.trim() || null,
    }
}

function shouldBlockForPlatform(
    appVersion: string,
    platform: NativeStorePlatform,
    minIos: string | null,
    minAndroid: string | null
): boolean {
    const min = platform === 'ios' ? minIos : minAndroid
    if (!min || isCalVerFloorDisabled(min)) {
        return false
    }
    if (!isValidCalVerRelease(min) || !isValidCalVerRelease(appVersion)) {
        return false
    }
    return isReleaseBelow(appVersion, min)
}

/** After a successful API response, enforce per-platform min CalVer from response headers. */
export async function applyDesignerMinAppVersionOnResponse(response: Response): Promise<void> {
    if (!import.meta.client || !response.ok) {
        return
    }
    const { capacitorApp, appVersion } = getRuntimePublicCompat()
    if (!capacitorApp) {
        return
    }

    const platform = await getNativePlatformForPolicy()
    if (!platform) {
        return
    }

    const { ios, android } = parseMinReleaseFromHeaders(response)
    if (!shouldBlockForPlatform(appVersion, platform, ios, android)) {
        return
    }

    const store = useForceUpdateStore()
    const minAppVersionByPlatform: Partial<Record<NativeStorePlatform, string>> = {}
    if (ios && isValidCalVerRelease(ios) && !isCalVerFloorDisabled(ios)) {
        minAppVersionByPlatform.ios = ios
    }
    if (android && isValidCalVerRelease(android) && !isCalVerFloorDisabled(android)) {
        minAppVersionByPlatform.android = android
    }

    store.blockFromPolicy({
        message: DEFAULT_BLOCK_MESSAGE,
        minAppVersionByPlatform,
        upgradeUrls: {},
    })
}

function parseJsonRecord(raw: unknown): Record<string, unknown> | null {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return raw as Record<string, unknown>
    }
    if (typeof raw === 'string') {
        try {
            const parsed: unknown = JSON.parse(raw)
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>
            }
        } catch {
            return null
        }
    }
    return null
}

/** Server `createError` responses often nest the payload under `data`; unwrap for `client_incompatible`. */
function parseClientIncompatiblePayload(_data: unknown): Record<string, unknown> | null {
    const rec = parseJsonRecord(_data)
    if (!rec) {
        return null
    }
    if (rec.error === 'client_incompatible') {
        return rec
    }
    const nested = parseJsonRecord(rec.data)
    if (nested && nested.error === 'client_incompatible') {
        return nested
    }
    return null
}

/**
 * Better Auth and other non-`useApiFetch` clients: turn a 403 response into the force-update overlay.
 */
export async function tryApplyClientIncompatibleFromResponse(response: Response): Promise<void> {
    if (!import.meta.client || response.status !== 403) {
        return
    }
    let data: unknown
    try {
        data = await response.clone().json()
    } catch {
        return
    }
    applyClientIncompatibleFromFetchError({ status: 403, _data: data })
}

/** When the API returns 403 `client_incompatible`, block the shell using the error body. */
export function applyClientIncompatibleFromFetchError(err: unknown): void {
    if (!import.meta.client) {
        return
    }
    const { capacitorApp } = getRuntimePublicCompat()
    if (!capacitorApp) {
        return
    }

    if (!err || typeof err !== 'object') {
        return
    }
    const e = err as { status?: number; _data?: unknown }
    if (e.status !== 403) {
        return
    }

    const rec = parseClientIncompatiblePayload(e._data)
    if (!rec) {
        return
    }

    const message =
        typeof rec.message === 'string' && rec.message.trim().length > 0
            ? rec.message.trim()
            : DEFAULT_BLOCK_MESSAGE

    const minAppVersionByPlatform: Partial<Record<NativeStorePlatform, string>> = {}
    const mins = parseJsonRecord(rec.minAppVersionByPlatform)
    if (mins) {
        if (typeof mins.ios === 'string' && isValidCalVerRelease(mins.ios)) {
            minAppVersionByPlatform.ios = mins.ios
        }
        if (typeof mins.android === 'string' && isValidCalVerRelease(mins.android)) {
            minAppVersionByPlatform.android = mins.android
        }
    }

    const upgradeUrls: Partial<Record<NativeStorePlatform, string>> = {}
    const urls = parseJsonRecord(rec.upgradeUrls)
    if (urls) {
        if (typeof urls.ios === 'string' && urls.ios) {
            upgradeUrls.ios = urls.ios
        }
        if (typeof urls.android === 'string' && urls.android) {
            upgradeUrls.android = urls.android
        }
    }

    useForceUpdateStore().blockFromPolicy({
        message,
        minAppVersionByPlatform,
        upgradeUrls,
    })
}

type DesignerFetchOptions = Record<string, unknown> & {
    baseURL?: string
    onResponse?: (context: { response: Response }) => void | Promise<void>
}

/**
 * Chains `onResponse` so successful API responses still run min-version enforcement
 * (defense in depth when status is 200).
 */
export function wrapFetchOptionsWithMinAppVersionOnResponse<T extends DesignerFetchOptions>(
    opts: T
): T {
    if (!import.meta.client) {
        return opts
    }

    const prior = opts.onResponse

    return {
        ...opts,
        async onResponse(context: unknown) {
            if (typeof prior === 'function') {
                await (prior as (c: unknown) => void | Promise<void>)(context)
            }
            const response = (context as { response?: Response }).response
            if (response) {
                await applyDesignerMinAppVersionOnResponse(response)
            }
        },
    } as T
}
